import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { useEffect, Suspense } from 'react';
import { useBackgroundImage } from "@/hooks/useBackgroundImage";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { GoogleAnalytics, trackPageView } from "@/components/GoogleAnalytics";
import { CookieConsent } from "@/components/CookieConsent";
import ErrorBoundary from "@/components/ErrorBoundary";
import { GlobalLiveRegion } from "@/components/LiveRegion";
import { SectionSkeleton } from "@/components/SectionSkeleton";
import Start from "./pages/Start";
import Checklists from "./pages/Checklists";
import NotFound from "./pages/NotFound";
import Auth from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import { SharedChecklist } from './pages/SharedChecklist';
import About from './pages/About';
import { ImageGeneration, ImageEnhancement } from './App.lazy';

const queryClient = new QueryClient();

// Replace with your actual GA4 Measurement ID
const GA_MEASUREMENT_ID = "G-VN9L4482WN";

const RouteTracker = ({ measurementId, enabled }: { measurementId: string; enabled: boolean }) => {
  const location = useLocation();

  useEffect(() => {
    if (enabled) {
      // Defer tracking to next tick to allow React Helmet to update document.title
      // This ensures document.title is current before we read it in trackPageView
      setTimeout(() => {
        trackPageView(measurementId, location.pathname + location.search);
      }, 0);
    }
  }, [location, enabled, measurementId]);

  return null;
};

const BackgroundWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div 
      className="min-h-screen bg-background overflow-x-hidden"
      role="application"
      aria-label="Triathlon Race Day Checklist Application"
    >
      {children}
    </div>
  );
};

const App = () => {
  const { analyticsEnabled } = useGoogleAnalytics(GA_MEASUREMENT_ID);

  // Disable automatic scroll restoration to allow manual control
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <GoogleAnalytics measurementId={GA_MEASUREMENT_ID} enabled={analyticsEnabled} />
          <Toaster />
          <Sonner />
          <GlobalLiveRegion />
          <CookieConsent />
          <BrowserRouter>
            <RouteTracker measurementId={GA_MEASUREMENT_ID} enabled={analyticsEnabled} />
            <BackgroundWrapper>
              <Suspense fallback={<SectionSkeleton />}>
                <Routes>
                  <Route path="/" element={<Checklists />} />
                  <Route path="/start" element={<Start />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  {/* Lazy-loaded admin routes */}
                  <Route path="/generate-image" element={<ImageGeneration />} />
                  <Route path="/enhance-image" element={<ImageEnhancement />} />
                  <Route path="/shared/:token" element={<SharedChecklist />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BackgroundWrapper>
          </BrowserRouter>
        </ErrorBoundary>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
